/**
 * Log Viewer - Main Component
 *
 * Wires together:
 * - Stream worker
 * - SourceBar
 * - StatusBar
 * - Log display
 */

import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useSearchParams } from "react-router-dom";
import { FixedSizeList as List } from "react-window";
import { StatusBar } from "./StatusBar";
import { ColumnPicker } from "./ColumnPicker";
import { ThreadFilter } from "./ThreadFilter";
import { StreamFilter } from "./StreamFilter";
import { CategoryFilter } from "./CategoryFilter";
import { LevelFilter } from "./LevelFilter";
import { TimestampFilter } from "./TimestampFilter";
import { ParserSelector } from "./ParserSelector";
import { processRunExists } from "./processrun-queries";
import { archiveRpcClient } from "../../services/archive-rpc-client";
import {
  WorkerCommand,
  WorkerEvent,
  WorkerState,
  LogMessage,
  StreamViewStatusUpdate,
  StreamSummary,
  LogColumn,
  ColumnConfig,
  LogParserType,
  LogSourceMode,
  ArchiveProgress,
} from "./types";
import { config } from "../../config/app-config";
import { ROUTES } from "../../routes/paths";
import "./LogViewer.scss";
import { AuthTokenManager } from "../../services/auth-token-manager";

// Default column configuration
const DEFAULT_COLUMNS: ColumnConfig[] = [
  {
    column: "natsSeq",
    label: "NATS Seq",
    defaultVisible: false,
    width: "80px",
  },
  {
    column: "recordSeq",
    label: "Record Seq",
    defaultVisible: false,
    width: "90px",
  },
  {
    column: "lineSeq",
    label: "Line Number",
    defaultVisible: false,
    width: "80px",
  },
  { column: "source", label: "Stream", defaultVisible: false, width: "80px" },
  {
    column: "fullTimestamp",
    label: "Timestamp",
    defaultVisible: false,
    width: "220px",
  },
  { column: "timestamp", label: "Time", defaultVisible: true, width: "150px" },
  { column: "level", label: "Level", defaultVisible: true, width: "70px" },
  { column: "thread", label: "Thread", defaultVisible: true, width: "120px" },
  // Shown on the lines that carry one: a checkpoint process's console, where each
  // line names the process run that ran that leg.
  { column: "processRun", label: "Process Run", defaultVisible: true, width: "90px" },
  {
    column: "category",
    label: "Category",
    defaultVisible: true,
    width: "150px",
  },
  { column: "text", label: "Message", defaultVisible: true, width: "1fr" },
  {
    column: "raw",
    label: "Original Line",
    defaultVisible: false,
    width: "1fr",
  },
  {
    column: "isContinuation",
    label: "Cont",
    defaultVisible: false,
    width: "50px",
  },
];

// Format full timestamp (YYYY-MM-DD HH:MM:SS.mmm)
function formatFullTimestamp(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const millis = String(date.getMilliseconds()).padStart(3, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${millis}`;
}

// Format time with milliseconds (HH:MM:SS.mmm)
function formatTimeWithMillis(date: Date): string {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const millis = String(date.getMilliseconds()).padStart(3, "0");
  return `${hours}:${minutes}:${seconds}.${millis}`;
}

// Map source numeric values to stream names
function formatStreamSource(source: string | undefined): string {
  if (!source) return "";
  if (source === "1") return "stderr";
  if (source === "2") return "stdout";
  return source; // Return as-is if not recognized
}

// Format number with comma separators
function formatNumber(num: number | undefined): string {
  if (num === undefined) return "";
  return num.toLocaleString();
}

export interface LogViewerProps {
  processUid: string;
  channel: string;
  /**
   * Optional start position from URL. Supports:
   * - Keywords: "first", "last"
   * - Offset: "100" (100 messages from end)
   * - Time: "5m", "1h", "30s", "2d"
   * - Both: "100+5m" (API picks whichever gives more messages)
   * - Exact: large number like "12345"
   */
  startSeq?: number | string;
  /**
   * Optional end position from URL. Supports same formats as start, plus:
   * - "tail" for live streaming
   */
  endSeq?: number | string;
  /**
   * Optional: start in raw mode (shows only originalLine column)
   */
  initialRawMode?: boolean;
  /**
   * Optional: parser type (defaults to 'structured')
   */
  parserType?: LogParserType;
  /**
   * Optional: log source mode (defaults to 'auto')
   * - 'auto': use archives first, then nats (default)
   * - 'archive-only': only use archives
   * - 'nats-only': only use nats live stream
   */
  sourceMode?: LogSourceMode;
  /**
   * Optional: the run's other log channels, so the status bar can offer a
   * chooser. Resolved by the page from the continuum.stream ledger.
   */
  channels?: string[];
  /**
   * Optional: the run had a mesh mailbox, so its RPC traffic can be viewed — the
   * chooser offers "Mesh traffic" alongside the log channels. Addressed by PROCESS
   * RUN, so no mailbox key is involved on the client at all.
   */
  hasMeshTraffic?: boolean;
}

export function LogViewer(props: LogViewerProps) {
  const { processUid, channel, channels, hasMeshTraffic, startSeq, endSeq, initialRawMode, parserType = 'structured', sourceMode = 'auto' } = props;

  // URL params for updating parser
  const [searchParams, setSearchParams] = useSearchParams();

  // Worker
  const workerRef = useRef<Worker | null>(null);

  // State
  const [workerState, setWorkerState] = useState<WorkerState>("idle");
  const createEmptySummary = (): StreamSummary => ({
    categoriesSet: new Set(),
    threadNamesSet: new Set(),
    streamNamesSet: new Set(),
    logLevelsSet: new Set(),
    minTimestamp: null,
    maxTimestamp: null,
    maxNatsSeq: null,
    minNatsSeq: null,
    seenSequences: new Set(),
    linesProcessed: 0,
    recordsProcessed: 0,
    bytesProcessed: 0,
    blocksProcessed: 0,
    lineCounter: 0,
    totalBytes: 0,
    totalBlocks: 0,
    streamInfo: null,
  });

  const [status, setStatus] = useState<StreamViewStatusUpdate>(() => ({
    workerState: "idle",
    range: { fromSeq: 0 },
    isLive: false,
    latestMaxSeq: undefined,
    lastSeenSeq: undefined,
    lag: undefined,
    streamInfo: undefined,
    summary: createEmptySummary(),
  }));
  const [messages, setMessages] = useState<LogMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Set when the run has no log stream and no archive. We then check whether
  // the process run actually EXISTS (a DB lookup, independent of the log
  // stream) to tell apart cases the channel_info 404 conflates:
  //   - "checking"  : existence lookup in flight
  //   - "no-logs"   : the process ran but produced no logs (normal empty state)
  //   - "not-found" : there is no such process run (a genuine 404)
  //   - "unknown"   : the existence lookup itself failed (can't say either way)
  // null = the viewer is not in a no-logs state.
  const [noLogs, setNoLogs] = useState<"checking" | "no-logs" | "not-found" | "unknown" | null>(null);
  const [archiveProgress, setArchiveProgress] = useState<ArchiveProgress | null>(null);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<Set<LogColumn>>(() => {
    if (initialRawMode) {
      return new Set(["raw"]);
    }
    return new Set(
      DEFAULT_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.column)
    );
  });

  // Text filter
  const [textFilter, setTextFilter] = useState<string>("");

  // Log level filter (selected levels to show)
  const [selectedLevels, setSelectedLevels] = useState<Set<string>>(new Set());
  const [allLevelsSelected, setAllLevelsSelected] = useState<boolean>(true);

  // Thread filter (selected threads to show)
  const [selectedThreads, setSelectedThreads] = useState<Set<string>>(
    new Set()
  );
  const [allThreadsSelected, setAllThreadsSelected] = useState<boolean>(true);

  // Stream filter (selected streams to show)
  const [selectedStreams, setSelectedStreams] = useState<Set<string>>(
    new Set()
  );
  const [allStreamsSelected, setAllStreamsSelected] = useState<boolean>(true);

  // Category filter (selected categories to show)
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
    new Set()
  );
  const [allCategoriesSelected, setAllCategoriesSelected] =
    useState<boolean>(true);

  // Timestamp filter (from/to timestamps)
  const [timestampFrom, setTimestampFrom] = useState<number>(0);
  const [timestampTo, setTimestampTo] = useState<number>(
    Number.MAX_SAFE_INTEGER
  );
  const [timestampFollowMode, setTimestampFollowMode] = useState<boolean>(true);

  // Get min/max timestamps from worker summary (updated every 5 seconds)
  const statusMinTimestamp = status.summary.minTimestamp ?? undefined;
  const statusMaxTimestamp = status.summary.maxTimestamp ?? undefined;
  const minTimestamp = statusMinTimestamp ?? Date.now();
  const maxTimestamp = statusMaxTimestamp ?? Date.now();

  // Initialize timestamp range when summary first arrives
  useEffect(() => {
    if (statusMinTimestamp && timestampFrom === 0) {
      setTimestampFrom(statusMinTimestamp);
      setTimestampTo(statusMaxTimestamp ?? statusMinTimestamp);
    }
  }, [statusMinTimestamp, statusMaxTimestamp, timestampFrom]);

  const handleColumnVisibilityChange = useCallback(
    (column: LogColumn, visible: boolean) => {
      setVisibleColumns((prev) => {
        const next = new Set(prev);
        if (visible) {
          next.add(column);
        } else {
          next.delete(column);
        }
        return next;
      });
    },
    []
  );

  const _handleResetColumns = useCallback(() => {
    setVisibleColumns(
      new Set(
        DEFAULT_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.column)
      )
    );
  }, []);

  const switchToParsedView = useCallback(() => {
    setVisibleColumns(
      new Set(["timestamp", "level", "thread", "processRun", "category", "text"])
    );
  }, []);

  const switchToRawView = useCallback(() => {
    setVisibleColumns(new Set(["raw"]));
  }, []);

  // Auto-scroll and dimensions
  const listRef = useRef<List>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(endSeq === 'tail');
  const [containerHeight, setContainerHeight] = useState(600);

  // Viewport anchor - track the lineSeq of the first visible item to maintain position during filtering
  const viewportAnchorRef = useRef<{
    lineSeq: number | undefined;
    offset: number;
  }>({ lineSeq: undefined, offset: 0 });

  // Measure container height
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // ============================================================================
  // Worker Event Handler
  // ============================================================================

  const handleWorkerEvent = useCallback((event: WorkerEvent) => {
    switch (event.type) {
      case "state_changed":
        setWorkerState(event.workerState);
        break;

      case "message":
        setMessages((prev) => {
          const newMessages = [...prev];
          newMessages.push(event.message);
          return newMessages;
        });
        break;

      case "messages_batch":
        setMessages((prev) => {
          const newMessages = [...prev];
          newMessages.push(...event.messages);
          return newMessages;
        });
        break;

      case "message_update":
        setMessages((prev) => {
          if (event.lineSeq < prev.length) {
            const updated = [...prev];
            updated[event.lineSeq] = event.updatedMessage;
            return updated;
          }
          return prev;
        });
        break;

      case "message_updates_batch":
        setMessages((prev) => {
          const updated = [...prev];
          let modified = false;
          for (const update of event.updates) {
            if (update.lineSeq < updated.length) {
              updated[update.lineSeq] = update;
              modified = true;
            }
          }
          return modified ? updated : prev;
        });
        break;

      case "status_update":
        setStatus(event.status);
        break;

      case "archive_progress":
        setArchiveProgress(event.progress);
        break;

      case "no_logs":
        // No live stream and no archive for this run. That alone can't tell us
        // whether the process ran-with-no-output vs. doesn't exist — the
        // channel_info 404 conflates both. Resolve it with a DB existence check
        // (different data path from the log stream) and show a precise message.
        setError(null);
        setNoLogs("checking");
        processRunExists(processUid)
          .then((exists) => setNoLogs(exists ? "no-logs" : "not-found"))
          .catch(() => setNoLogs("unknown")); // existence lookup failed — can't say either way
        break;

      case "error":
        setError(event.error);
        setWorkerState("error");
        break;

      case "archive_rpc_request":
        // The worker can't own the authenticated WS client, so archive.v1
        // control calls run here on the user's session; the answer routes
        // back by id. Bytes never relay — the worker fetches the capability
        // URL itself.
        archiveRpcClient
          .call(event.endPoint, event.request)
          .then((response) => {
            workerRef.current?.postMessage({
              type: "archive_rpc_response",
              id: event.id,
              response,
            } as WorkerCommand);
          })
          .catch((err) => {
            workerRef.current?.postMessage({
              type: "archive_rpc_response",
              id: event.id,
              error: err instanceof Error ? err.message : String(err),
            } as WorkerCommand);
          });
        break;
    }
  }, []);

  // ============================================================================
  // Unique Values Extraction
  // ============================================================================

  // Get unique values from worker summary (updated every 5 seconds)
  const uniqueThreads = Array.from(status.summary.threadNamesSet);
  const uniqueStreams = Array.from(status.summary.streamNamesSet);
  const uniqueCategories = Array.from(status.summary.categoriesSet);
  const uniqueLevels = Array.from(status.summary.logLevelsSet);

  // Auto-add newly discovered items when "all selected" flag is true
  useEffect(() => {
    if (allLevelsSelected) {
      setSelectedLevels((prev) => {
        const newSet = new Set(prev);
        let changed = false;
        for (const level of uniqueLevels) {
          if (!newSet.has(level)) {
            newSet.add(level);
            changed = true;
          }
        }
        return changed ? newSet : prev;
      });
    }
  }, [uniqueLevels, allLevelsSelected]);

  useEffect(() => {
    if (allThreadsSelected) {
      setSelectedThreads((prev) => {
        const newSet = new Set(prev);
        let changed = false;
        for (const thread of uniqueThreads) {
          if (!newSet.has(thread)) {
            newSet.add(thread);
            changed = true;
          }
        }
        return changed ? newSet : prev;
      });
    }
  }, [uniqueThreads, allThreadsSelected]);

  useEffect(() => {
    if (allStreamsSelected) {
      setSelectedStreams((prev) => {
        const newSet = new Set(prev);
        let changed = false;
        for (const stream of uniqueStreams) {
          if (!newSet.has(stream)) {
            newSet.add(stream);
            changed = true;
          }
        }
        return changed ? newSet : prev;
      });
    }
  }, [uniqueStreams, allStreamsSelected]);

  useEffect(() => {
    if (allCategoriesSelected) {
      setSelectedCategories((prev) => {
        const newSet = new Set(prev);
        let changed = false;
        for (const category of uniqueCategories) {
          if (!newSet.has(category)) {
            newSet.add(category);
            changed = true;
          }
        }
        return changed ? newSet : prev;
      });
    }
  }, [uniqueCategories, allCategoriesSelected]);

  // ============================================================================
  // Filtered Messages
  // ============================================================================

  // Helper to check if a message's level is selected
  const meetsLogLevel = useCallback(
    (msg: LogMessage): boolean => {
      // If "all levels selected" flag is true, show all
      if (allLevelsSelected) return true;

      // Otherwise check if level is in the selected set
      if (!msg.level) return true; // Show messages with no level
      const msgLevel = msg.level.toUpperCase();
      return selectedLevels.has(msgLevel);
    },
    [selectedLevels, allLevelsSelected]
  );

  // Memoized filtered messages - only recomputes when messages, textFilter, selectedLevels, selectedThreads, selectedStreams, or selectedCategories changes
  // For structured logs: Groups continuation lines with their parent (if any message in a group matches, include all messages in that group)
  // For plain-text logs: Direct filtering without continuation grouping
  const filteredMessages = useMemo(() => {
    // Record-based parsers (plain-text, journal, eventlog) emit one standalone
    // entry per line/record — no continuation children — so filter directly
    // without the structured-log continuation grouping.
    if (parserType === 'plain-text' || parserType === 'journal' || parserType === 'eventlog') {
      const result = messages.filter((msg) => {
        // Apply log level filter
        if (!meetsLogLevel(msg)) {
          return false;
        }

        // Apply thread filter
        if (!allThreadsSelected) {
          if (!msg.thread || !selectedThreads.has(msg.thread)) {
            return false;
          }
        }

        // Apply stream filter
        if (!allStreamsSelected) {
          if (!msg.source || !selectedStreams.has(msg.source)) {
            return false;
          }
        }

        // Apply category filter
        if (!allCategoriesSelected) {
          if (!msg.category || !selectedCategories.has(msg.category)) {
            return false;
          }
        }

        // Apply timestamp filter
        if (msg.timestamp) {
          const msgTime = msg.timestamp.getTime();
          // Check lower bound always
          if (msgTime < timestampFrom) {
            return false;
          }
          // Skip upper bound check when in follow mode (sticky to end)
          if (!timestampFollowMode && msgTime > timestampTo) {
            return false;
          }
        }

        // Apply text filter
        const trimmedFilter = textFilter.trim();
        if (trimmedFilter) {
          const lowerFilter = trimmedFilter.toLowerCase();
          const matchesText =
            msg.text?.toLowerCase().includes(lowerFilter) ||
            msg.originalLine?.toLowerCase().includes(lowerFilter) ||
            msg.category?.toLowerCase().includes(lowerFilter) ||
            msg.thread?.toLowerCase().includes(lowerFilter) ||
            msg.level?.toLowerCase().includes(lowerFilter);
          if (!matchesText) {
            return false;
          }
        }

        return true;
      });

      return result;
    }

    // Structured parser: two-pass filtering with continuation grouping
    // First pass: identify natsSeq values that have at least one matching message
    const matchingNatsSeqs = new Set<number>();

    for (const msg of messages) {
      // Apply log level filter
      if (!meetsLogLevel(msg)) {
        continue;
      }

      // Apply thread filter
      if (!allThreadsSelected) {
        if (!msg.thread || !selectedThreads.has(msg.thread)) {
          continue;
        }
      }

      // Apply stream filter
      if (!allStreamsSelected) {
        if (!msg.source || !selectedStreams.has(msg.source)) {
          continue;
        }
      }

      // Apply category filter
      if (!allCategoriesSelected) {
        if (!msg.category || !selectedCategories.has(msg.category)) {
          continue;
        }
      }

      // Apply timestamp filter
      if (msg.timestamp) {
        const msgTime = msg.timestamp.getTime();
        // Check lower bound always
        if (msgTime < timestampFrom) {
          continue;
        }
        // Skip upper bound check when in follow mode (sticky to end)
        if (!timestampFollowMode && msgTime > timestampTo) {
          continue;
        }
      }

      // Apply text filter
      const trimmedFilter = textFilter.trim();
      if (trimmedFilter) {
        const lowerFilter = trimmedFilter.toLowerCase();
        const matchesText =
          msg.text?.toLowerCase().includes(lowerFilter) ||
          msg.originalLine?.toLowerCase().includes(lowerFilter) ||
          msg.category?.toLowerCase().includes(lowerFilter) ||
          msg.thread?.toLowerCase().includes(lowerFilter) ||
          msg.level?.toLowerCase().includes(lowerFilter);
        if (!matchesText) {
          continue;
        }
      }

      // This message passed all filters - mark its natsSeq as matching
      matchingNatsSeqs.add(msg.natsSeq);
    }

    // Second pass: include all messages belonging to matching natsSeq values
    // This ensures continuation lines are shown when any part of the group matches
    const result = messages.filter((msg) => matchingNatsSeqs.has(msg.natsSeq));

    return result;
  }, [
    messages,
    textFilter,
    selectedThreads,
    selectedStreams,
    selectedCategories,
    allThreadsSelected,
    allStreamsSelected,
    allCategoriesSelected,
    timestampFrom,
    timestampTo,
    timestampFollowMode,
    meetsLogLevel,
    parserType,
  ]);

  // ============================================================================
  // Worker Lifecycle
  // ============================================================================

  // Initialize worker
  useEffect(() => {
    const worker = new Worker(new URL("./stream.worker.ts", import.meta.url), {
      type: "module",
    });

    worker.onmessage = (event: MessageEvent<WorkerEvent>) => {
      handleWorkerEvent(event.data);
    };

    workerRef.current = worker;

    return () => {
      worker.terminate();
    };
  }, [handleWorkerEvent]);

  // Track viewport anchor on scroll (only when not auto-scrolling)
  const handleItemsRendered = useCallback(
    ({ visibleStartIndex }: { visibleStartIndex: number }) => {
      if (
        !autoScroll &&
        filteredMessages.length > 0 &&
        visibleStartIndex < filteredMessages.length
      ) {
        const firstVisibleMsg = filteredMessages[visibleStartIndex];
        if (firstVisibleMsg && firstVisibleMsg.lineSeq !== undefined) {
          viewportAnchorRef.current = {
            lineSeq: firstVisibleMsg.lineSeq,
            offset: visibleStartIndex,
          };
        }
      }
    },
    [autoScroll, filteredMessages]
  );

  // Restore scroll position when filter changes (maintain anchor)
  useEffect(() => {
    if (autoScroll || !listRef.current || filteredMessages.length === 0) {
      return;
    }

    const anchor = viewportAnchorRef.current;
    if (anchor.lineSeq !== undefined) {
      // Find the new index of the anchored message in filtered results
      const newIndex = filteredMessages.findIndex(
        (msg) => msg.lineSeq === anchor.lineSeq
      );
      if (newIndex >= 0) {
        // Scroll to maintain the same message at roughly the same position
        listRef.current.scrollToItem(newIndex, "start");
      }
    }
  }, [filteredMessages, autoScroll]);

  // Auto-scroll to bottom when messages change (and auto-scroll is enabled)
  useEffect(() => {
    if (autoScroll && listRef.current && filteredMessages.length > 0) {
      // Scroll to the padding row (last index) so the last actual message is fully visible
      listRef.current.scrollToItem(filteredMessages.length, "end");
    }
  }, [filteredMessages, autoScroll]);

  // ============================================================================
  // Scroll Navigation
  // ============================================================================

  const scrollToTop = useCallback(() => {
    if (listRef.current && filteredMessages.length > 0) {
      listRef.current.scrollToItem(0, "start");
    }
  }, [filteredMessages.length]);

  const scrollToBottom = useCallback(() => {
    if (listRef.current && filteredMessages.length > 0) {
      // Scroll to the padding row so the last actual message is fully visible
      listRef.current.scrollToItem(filteredMessages.length, "end");
    }
  }, [filteredMessages.length]);

  // ============================================================================
  // Commands
  // ============================================================================

  const sendCommand = useCallback((command: WorkerCommand) => {
    if (workerRef.current) {
      workerRef.current.postMessage(command);
    }
  }, []);

  const play = useCallback(async () => {
    const finalToken = AuthTokenManager.getInstance().getToken();

    if (!finalToken) {
      setError("No auth token available. Please login first.");
      return;
    }

    // Parse and resolve position specs using the DSL
    let resolvedFromSeq: number | undefined = undefined;
    let resolvedToSeq: number | undefined = undefined;

    try {
      const { parsePosition, resolvePosition } = await import("./position-dsl");

      // Parse start position
      const startSpec = parsePosition(startSeq);
      if (startSpec) {
        resolvedFromSeq = await resolvePosition(
          startSpec,
          processUid,
          channel,
          finalToken,
          config.hermes.url,
          "start"
        );
      }

      // Parse end position
      const endSpec = parsePosition(endSeq);
      if (endSpec) {
        resolvedToSeq = await resolvePosition(
          endSpec,
          processUid,
          channel,
          finalToken,
          config.hermes.url,
          "end"
        );
      }
    } catch (err) {
      setError(`Failed to resolve position: ${err}`);
      return;
    }

    const normalizedFrom = resolvedFromSeq !== undefined ? resolvedFromSeq : 0;
    const range = {
      fromSeq: normalizedFrom < 0 ? 0 : normalizedFrom,
      toSeq: resolvedToSeq,
    };

    sendCommand({
      type: "play",
      config: {
        processUid,
        channel,
        range,
        authToken: finalToken,
        hermesUrl: config.hermes.url,
        parserType,
        sourceMode,
      },
    });
    setMessages([]); // Clear old messages
    setError(null);
    setNoLogs(null);
  }, [processUid, channel, sendCommand, startSeq, endSeq, parserType]);

  const handleParserChange = useCallback((newParser: LogParserType) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('parser', newParser);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Auto-play on mount (only once per processUid/channel)
  const hasAutoPlayedRef = useRef(false);
  const lastSourceRef = useRef({ processUid: "", channel: "" });

  useEffect(() => {
    // Reset auto-play flag when source changes
    if (
      lastSourceRef.current.processUid !== processUid ||
      lastSourceRef.current.channel !== channel
    ) {
      hasAutoPlayedRef.current = false;
      lastSourceRef.current = { processUid, channel };
    }

    if (
      workerRef.current &&
      workerState === "idle" &&
      !hasAutoPlayedRef.current
    ) {
      hasAutoPlayedRef.current = true;
      play();
    }
  }, [processUid, channel, play, workerState]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="log-viewer">
      {/* Status Bar */}
      <StatusBar
        status={status}
        processUid={processUid}
        channel={channel}
        channels={channels}
        hasMeshTraffic={hasMeshTraffic}
        totalCount={messages.length}
        filteredCount={filteredMessages.length}
        archiveProgress={archiveProgress}
      />

      {/* Controls */}
      <div className="log-viewer__controls">
        <a
          href={`${config.hermes.url}/api/stream_download/${processUid}/${channel}`}
          target="_blank"
          rel="noopener noreferrer"
          className="log-viewer__button log-viewer__button--link"
        >
          Download
        </a>

        {/* Parser Selector. The channel decides which parsers it can offer (and
            whether there is any choice at all) — see channels.ts. */}
        <ParserSelector
          parserType={parserType}
          onParserChange={handleParserChange}
          channel={channel}
        />

        <ColumnPicker
          columns={DEFAULT_COLUMNS}
          visibleColumns={visibleColumns}
          onVisibilityChange={handleColumnVisibilityChange}
          onSwitchToParsed={switchToParsedView}
          onSwitchToRaw={switchToRawView}
        />

        {/* Log Level Filter */}
        <LevelFilter
          levels={uniqueLevels}
          selectedLevels={selectedLevels}
          onSelectionChange={setSelectedLevels}
          allSelected={allLevelsSelected}
          onAllSelectedChange={setAllLevelsSelected}
        />

        {/* Thread Filter */}
        <ThreadFilter
          threads={uniqueThreads}
          selectedThreads={selectedThreads}
          onSelectionChange={setSelectedThreads}
          allSelected={allThreadsSelected}
          onAllSelectedChange={setAllThreadsSelected}
        />

        {/* Stream Filter */}
        <StreamFilter
          streams={uniqueStreams}
          selectedStreams={selectedStreams}
          onSelectionChange={setSelectedStreams}
          allSelected={allStreamsSelected}
          onAllSelectedChange={setAllStreamsSelected}
        />

        {/* Category Filter */}
        <CategoryFilter
          categories={uniqueCategories}
          selectedCategories={selectedCategories}
          onSelectionChange={setSelectedCategories}
          allSelected={allCategoriesSelected}
          onAllSelectedChange={setAllCategoriesSelected}
        />

        {/* Timestamp Filter */}
        {messages.length > 0 && (
          <TimestampFilter
            minTimestamp={minTimestamp}
            maxTimestamp={maxTimestamp}
            fromTimestamp={timestampFrom}
            toTimestamp={timestampTo}
            onRangeChange={(from, to) => {
              setTimestampFrom(from);
              setTimestampTo(to);
            }}
            onFollowModeChange={setTimestampFollowMode}
          />
        )}

        {/* Text Filter */}
        <input
          type="text"
          placeholder="Filter logs..."
          value={textFilter}
          onChange={(e) => setTextFilter(e.target.value)}
          className="log-viewer__filter-input"
        />

        <label className="log-viewer__checkbox">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.target.checked)}
          />
          Auto-scroll
        </label>

        {/* Navigation Buttons */}
        <button
          onClick={scrollToTop}
          disabled={filteredMessages.length === 0}
          className="log-viewer__button"
          title="Scroll to top"
        >
          ⬆ Top
        </button>
        <button
          onClick={scrollToBottom}
          disabled={filteredMessages.length === 0}
          className="log-viewer__button"
          title="Scroll to bottom"
        >
          ⬇ Bottom
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="log-viewer__error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* No-stream notice. The DB existence check distinguishes "process ran
          but produced no logs" (normal) from "no such process run" (a real
          404). "checking" shows while that lookup is in flight. */}
      {noLogs === "checking" && !error && (
        <div className="log-viewer__notice">Checking process run…</div>
      )}
      {noLogs === "no-logs" && !error && (
        <div className="log-viewer__notice">
          This process ran but produced no logs. It wrote no console output —
          normal for short RPC-style jobs (for example
          <code> worker capture-metrics</code>), which register no log channel.
        </div>
      )}
      {noLogs === "not-found" && !error && (
        <div className="log-viewer__error">
          <strong>Process run not found:</strong> no process run exists with uid{" "}
          <code>{processUid}</code>.
        </div>
      )}
      {noLogs === "unknown" && !error && (
        <div className="log-viewer__notice">
          No logs found for this run, and the process run could not be verified.
        </div>
      )}

      {/* Log Display */}
      <div className="log-viewer__logs" ref={containerRef}>
        {noLogs === "no-logs" && filteredMessages.length === 0 && (
          <div className="log-viewer__empty">
            This process exists but has no logs.
          </div>
        )}
        {/* Empty-with-no-verdict is ALWAYS transient, so it reads as loading and
            never as an answer. workerState is "idle" BEFORE the auto-play effect
            starts the read, not after it finishes, so keying the empty state off
            "idle" showed a no-messages notice on every single load — and told the
            operator to "click Play", a control this toolbar does not have.
            A run that truly has nothing is reported by the noLogs branches above
            ("checking" -> no-logs / not-found / unknown), which own every terminal
            empty state; there is a beat between the reader going quiet and that
            verdict landing, and this covers it. */}
        {!noLogs &&
          filteredMessages.length === 0 &&
          messages.length === 0 &&
          workerState !== "consuming" && (
            <div className="log-viewer__empty">Loading logs…</div>
          )}
        {filteredMessages.length === 0 &&
          messages.length === 0 &&
          workerState === "consuming" && (
            <div className="log-viewer__empty">Waiting for messages...</div>
          )}
        {filteredMessages.length === 0 && messages.length > 0 && (
          <div className="log-viewer__empty">
            No messages match the current filter.
          </div>
        )}
        {filteredMessages.length > 0 && containerHeight > 0 && (
          <List
            ref={listRef}
            height={containerHeight}
            itemCount={filteredMessages.length + 1}
            itemSize={20}
            width="100%"
            onItemsRendered={handleItemsRendered}
          >
            {({ index, style }) => {
              // Render empty padding row at the end so last actual row is fully visible
              if (index >= filteredMessages.length) {
                return <div style={style} className="log-viewer__line"></div>;
              }

              const msg = filteredMessages[index];
              return (
                <div
                  style={{ ...style, overflow: "hidden" }}
                  className={`log-viewer__line log-viewer__line--${
                    msg.level?.toLowerCase() || "info"
                  }`}
                >
                  {visibleColumns.has("natsSeq") && (
                    <span className="log-viewer__seq">
                      {formatNumber(msg.natsSeq)}
                    </span>
                  )}
                  {visibleColumns.has("recordSeq") && (
                    <span className="log-viewer__seq">
                      {formatNumber(msg.recordSeq)}
                    </span>
                  )}
                  {visibleColumns.has("lineSeq") &&
                    msg.lineSeq !== undefined && (
                      <span className="log-viewer__seq">
                        {formatNumber(msg.lineSeq)}
                      </span>
                    )}
                  {visibleColumns.has("source") &&
                    msg.source &&
                    !msg.isContinuation && (
                      <span className="log-viewer__source">
                        {formatStreamSource(msg.source)}
                      </span>
                    )}
                  {visibleColumns.has("source") && msg.isContinuation && (
                    <span className="log-viewer__source"></span>
                  )}
                  {visibleColumns.has("fullTimestamp") &&
                    !msg.isContinuation && (
                      <span className="log-viewer__full-timestamp">
                        {msg.timestamp
                          ? formatFullTimestamp(msg.timestamp)
                          : ""}
                      </span>
                    )}
                  {visibleColumns.has("fullTimestamp") &&
                    msg.isContinuation && (
                      <span className="log-viewer__full-timestamp"></span>
                    )}
                  {visibleColumns.has("timestamp") && !msg.isContinuation && (
                    <span className="log-viewer__time">
                      {msg.timestamp ? formatTimeWithMillis(msg.timestamp) : ""}
                    </span>
                  )}
                  {visibleColumns.has("timestamp") && msg.isContinuation && (
                    <span className="log-viewer__time"></span>
                  )}
                  {visibleColumns.has("level") &&
                    msg.level &&
                    !msg.isContinuation && (
                      <span className="log-viewer__level">{msg.level}</span>
                    )}
                  {visibleColumns.has("level") && msg.isContinuation && (
                    <span className="log-viewer__level"></span>
                  )}
                  {visibleColumns.has("thread") &&
                    msg.thread &&
                    !msg.isContinuation && (
                      <span className="log-viewer__thread">{msg.thread}</span>
                    )}
                  {visibleColumns.has("thread") && msg.isContinuation && (
                    <span className="log-viewer__thread"></span>
                  )}
                  {visibleColumns.has("processRun") && msg.processRunUid && (
                    <a
                      className="log-viewer__process-run"
                      href={ROUTES.APP.LOGS_WITH_PARAMS(msg.processRunUid)}
                      target="_blank"
                      rel="noreferrer"
                      title={`process run ${msg.processRunUid}: open its own log`}
                      data-testid="log-process-run"
                    >
                      {msg.isContinuation ? "" : msg.processRunUid.slice(0, 8)}
                    </a>
                  )}
                  {visibleColumns.has("category") &&
                    msg.category &&
                    !msg.isContinuation && (
                      <span className="log-viewer__category">
                        {msg.category}
                      </span>
                    )}
                  {visibleColumns.has("category") && msg.isContinuation && (
                    <span className="log-viewer__category"></span>
                  )}
                  {visibleColumns.has("text") && (
                    <span className="log-viewer__text">{msg.text}</span>
                  )}
                  {visibleColumns.has("raw") && msg.originalLine && (
                    <span className="log-viewer__text">{msg.originalLine}</span>
                  )}
                  {visibleColumns.has("isContinuation") && (
                    <span className="log-viewer__continuation">
                      {msg.isContinuation ? "✓" : ""}
                    </span>
                  )}
                </div>
              );
            }}
          </List>
        )}
      </div>
    </div>
  );
}
